/**
 * =============================================================================
 * IDS CRM - A list page that comes back as it was left
 * =============================================================================
 *
 * Two hooks for a list page (see utils/listReturn.js for the why):
 *
 *   const memory = useListMemory('/tickets');
 *   const [status, setStatus] = useState(() => memory.initial('status', ''));
 *   ...
 *   useRememberList('/tickets', { status, page });
 *
 * `initial` gives back the remembered value only when the page is being
 * returned to: the browser's Back or Forward button (or a reload), or a "←"
 * link. A fresh visit gets the fallback, so the sidebar still opens a clean
 * list. (A typed address or a bookmark wipes the memory as the page loads:
 * see forgetListsOnFreshLoad in utils/listReturn.js.)
 * =============================================================================
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { readList, rememberList, touchList } from '../utils/listReturn';

export function useListMemory(path) {
  const location = useLocation();
  const navigationType = useNavigationType();

  // Decided once, on the first render, before the page's own filters exist.
  // The address must be the one the list was left on: a different address
  // (a link pasted into the tab, a dashboard link) is a fresh visit, and what
  // that address asks for wins.
  const first = useRef(null);
  if (first.current === null) {
    const comingBack = navigationType === 'POP' || Boolean(location.state && location.state.restoreList);
    const saved = comingBack ? readList(path) : null;
    const returning = Boolean(saved) && saved.search === location.search;
    first.current = { returning, view: returning ? saved.view : {} };
  }

  const initial = useCallback((name, fallback) => (
    Object.prototype.hasOwnProperty.call(first.current.view, name) ? first.current.view[name] : fallback
  ), []);

  return { initial, returning: first.current.returning };
}

/**
 * Keeps the memory up to date: the list's address and `view`, the filters it
 * holds on screen only. `view` must be plain values (it is stored as JSON).
 */
export function useRememberList(path, view = {}) {
  const location = useLocation();
  const viewJson = JSON.stringify(view);
  useEffect(() => {
    rememberList(path, { search: location.search, view: JSON.parse(viewJson) });
  }, [path, location.search, viewJson]);
  // Leaving the list: its memory counts from now, so a list that sat on
  // screen all night still comes back when a row is opened in the morning.
  useEffect(() => () => touchList(path), [path]);
}

export default useListMemory;
